import React, { useEffect, useState } from 'react';
import useBundleTranslation from 'i18n';
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import { Popup } from 'components/common/popup/Popup';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { useParams } from 'react-router-dom';
import { alertRuleAPI } from 'api/alert-wizard';
import { useMiNavigation } from 'hooks/useNavigation';
import AlertRulePopup from 'components/alert-rule-popup/AlertRulePopup';

interface metricOption {
    element_id: number;
    name: string;
    segment_name: string | null;
}

interface popupMainData {
    isAllowed2CreateNewMetric: boolean;
    metricsList: metricOption[];
    userId: number;
}

export interface AddNewAlertPopupProps {
    handleHidePopup: () => void;
    reloadGridData: () => void;
    componentProps?: any;
}

//engine/application/editor/views/scripts/alertrule/get-metric-for-new-alert-popup.phtml
export default function AddNewAlertPopup(props: AddNewAlertPopupProps) {
    const { handleHidePopup = () => {}, componentProps, reloadGridData = () => {} } = props;

    const { t } = useBundleTranslation(['components/alert-rule-popup/add_alert_popup']);
    const [mainData, setMainData] = useState<popupMainData>();
    const [selectedMetric, setSelectedMetric] = useState<string>(''); //metric_element_id
    const [metricType, setMetricType] = useState<string>('new');
    const navigate = useMiNavigation();
    const [open, setOpen] = useState<boolean>(true);
    const [showAlertWizardPopup, setShowAlertWizardPopup] = useState<boolean>(false);
    const urlParams = useParams();

    useEffect(() => {
        if (componentProps.type === 'metricEditor') {
            setOpen(false);
            if (urlParams.elementId) {
                setMetricType('exist');
                setSelectedMetric(urlParams.elementId);
            }
            setShowAlertWizardPopup(true);
        } else {
            alertRuleAPI
                .createNewAlertRule()
                .then((response) => {
                    if (response.data.status === 'OK') {
                        const data = response.data.data;
                        setMainData(data);
                        setSelectedMetric(data?.metricsList?.[0]?.element_id ?? '');
                        if (!data.isAllowed2CreateNewMetric) {
                            setMetricType('exist');
                        }
                    }
                })
                .catch(() => {});
        }
    }, []);

    return (
        <>
            {mainData && open && (
                <Popup
                    settings={{
                        title: t('popup_title'),
                        textOK: t('apply_btn'),
                        noCancel: true,
                    }}
                    maxWidth="popupMd"
                    open={open}
                    onHide={() => {
                        setOpen(false);
                        handleHidePopup();
                    }}
                    onConfirm={() => {
                        setOpen(false);
                        if ('new' == metricType) {
                            navigate(`/editor/element/metric/new?newkpi=Y`);
                        } else {
                            setShowAlertWizardPopup(true);
                        }
                    }}
                >
                    {mainData.isAllowed2CreateNewMetric && (
                        <Box sx={{ mb: (theme) => theme.size.pxValue(theme.size.componentFormIndent) }}>
                            <FormControl>
                                <RadioGroup
                                    name="metric-assign-type"
                                    onChange={(newVal) => {
                                        setMetricType(newVal.target.value);
                                    }}
                                    value={String(metricType)}
                                >
                                    <FormControlLabel value={'new'} control={<Radio />} label={t(`option_new`)} />
                                    <FormControlLabel value={'exist'} control={<Radio />} label={t(`option_exist`)} />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}
                    {metricType == 'exist' && (
                        <>
                            {!mainData.isAllowed2CreateNewMetric && <FormLabel>{t('option_exist')}</FormLabel>}
                            <Box>
                                <ReactSelect
                                    data={(mainData?.metricsList || []).map((item) => {
                                        return {
                                            value: String(item.element_id),
                                            label:
                                                item.name +
                                                (item.segment_name
                                                    ? ' ' + t('dimension_label', { segmentName: item.segment_name })
                                                    : ''),
                                        };
                                    })}
                                    value={selectedMetric}
                                    update={(val) => {
                                        setSelectedMetric(val);
                                    }}
                                    dataTest={'new-kpi-existing-metric'}
                                />
                            </Box>
                        </>
                    )}
                </Popup>
            )}
            {showAlertWizardPopup && (
                <AlertRulePopup
                    elementId={metricType == 'exist' ? Number(selectedMetric) : undefined}
                    onClose={() => {
                        setShowAlertWizardPopup(false);
                        handleHidePopup();
                    }}
                    onFinish={(response) => {
                        if (response.data.status == 'OK') {
                            if (componentProps.type === 'metricEditor') {
                                reloadGridData();
                            } else {
                                navigate('/editor/kpi-alert-rule/' + response.data.id);
                            }
                        }
                        setShowAlertWizardPopup(false);
                        handleHidePopup();
                    }}
                    show={true}
                />
            )}
        </>
    );
}
